import React, { useState } from "react";
import "../styles/add-team-member-modal.css";
import "../styles/program-document-modal.css";
import {
    confirmProgramUpload,
    renameProgramDocument,
    requestProgramUploadUrl,
} from "../services/programDocumentApi";

const ACCEPT =
    ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.odt,.ods,.odp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Shown in the modal; technical details go to console for developers. */
const MSG_UPLOAD_NETWORK =
    "We couldn’t upload your file. Please check your internet connection and try again. If this keeps happening, contact your administrator or Polaris support.";
const MSG_UPLOAD_DENIED =
    "We couldn’t save your file because the upload was denied. Please try again or contact your administrator.";
const MSG_UPLOAD_FAILED =
    "We couldn’t finish uploading your file. Please try again or choose a different file.";

function logProgramUploadDiag(message: string, extra?: unknown) {
    console.warn("[Program document upload]", message, extra ?? "");
}

type Props = {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    companyId?: number | null;
    editDocumentId?: number | null;
    initialName?: string;
};

export default function ProgramDocumentUploadModal({
    open,
    onClose,
    onSuccess,
    companyId = null,
    editDocumentId = null,
    initialName = "",
}: Props) {
    const [name, setName] = useState(initialName);
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    React.useEffect(() => {
        if (open) {
            setName(initialName);
            setFile(null);
            setError(null);
            setProgress(null);
            setSubmitting(false);
            setDragOver(false);
        }
    }, [open, initialName]);

    if (!open) return null;

    const validateName = (n: string) => {
        const t = n.trim();
        if (t.length < 5 || t.length > 50) {
            return "Resource name must be between 5 and 50 characters.";
        }
        return null;
    };

    const pickFile = (f: File | null | undefined) => {
        if (f) setFile(f);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        pickFile(f);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const nameErr = validateName(name);
        if (nameErr) {
            setError(nameErr);
            return;
        }

        setError(null);
        setSubmitting(true);

        try {
            if (editDocumentId != null && !file) {
                await renameProgramDocument(editDocumentId, name.trim());
                onSuccess();
                onClose();
                return;
            }

            if (!file) {
                setError("Choose a file to upload.");
                setSubmitting(false);
                return;
            }

            const up = await requestProgramUploadUrl(file.name, companyId);
            setProgress(0);

            await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("PUT", up.uploadUrl);
                xhr.setRequestHeader("Content-Type", up.contentType);
                xhr.upload.onprogress = (ev) => {
                    if (ev.lengthComputable) {
                        setProgress(Math.round((ev.loaded / ev.total) * 100));
                    }
                };
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) resolve();
                    else {
                        if (xhr.status === 403) {
                            logProgramUploadDiag(
                                "S3 returned 403 after PUT. Common causes: CORS not allowing this app origin, IAM denying PutObject, wrong AWS region vs bucket, or presigned URL mismatch. See polaris-service/docs/program-documents-s3-cors.md."
                            );
                            reject(new Error(MSG_UPLOAD_DENIED));
                        } else {
                            logProgramUploadDiag(`Upload failed with HTTP ${xhr.status}`, xhr.responseText?.slice?.(0, 500));
                            reject(new Error(MSG_UPLOAD_FAILED));
                        }
                    }
                };
                xhr.onerror = () => {
                    logProgramUploadDiag(
                        "Browser blocked or failed the upload request before a normal HTTP response (often S3 CORS: the file storage bucket must allow PUT from this app’s URL, e.g. http://localhost:3000). See polaris-service/docs/program-documents-s3-cors.md."
                    );
                    reject(new Error(MSG_UPLOAD_NETWORK));
                };
                xhr.send(file);
            });

            await confirmProgramUpload({
                s3Key: up.s3Key,
                name: name.trim(),
                companyId,
                documentId: editDocumentId,
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err?.message || "Something went wrong.");
        } finally {
            setSubmitting(false);
            setProgress(null);
        }
    };

    const title = editDocumentId ? "Edit Resource" : "Add Resources";
    const submitLabel = editDocumentId && !file ? "Update resource" : "Add resource";

    return (
        <div className="team-modal" role="dialog" aria-modal="true" aria-labelledby="program-doc-modal-title">
            <div className="team-modal-card team-modal-card--confirm program-document-modal-card">
                <button type="button" className="team-modal-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
                <form onSubmit={handleSubmit} className="team-modal-content team-modal-content--confirm">
                    <h2 id="program-doc-modal-title" className="team-modal-confirm-title">
                        {title}
                    </h2>

                    <div className="program-document-field">
                        <label htmlFor="resource-name" className="program-document-label">
                            Resource name
                        </label>
                        <input
                            id="resource-name"
                            type="text"
                            className="program-document-input"
                            placeholder="Resource Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={submitting}
                            autoComplete="off"
                        />
                    </div>

                    <div
                        className={`program-document-dropzone ${dragOver ? "program-document-dropzone--drag" : ""}`}
                        onDragEnter={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragOver(true);
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragOver(false);
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onDrop={handleDrop}
                    >
                        <label className="program-document-dropzone-text">
                            <input
                                type="file"
                                accept={ACCEPT}
                                style={{ display: "none" }}
                                onChange={(e) => pickFile(e.target.files?.[0])}
                                disabled={submitting}
                            />
                            Choose a file or drag it here
                        </label>
                        <p className="program-document-dropzone-hint">
                            PDF, Word, Excel, PowerPoint, OpenDocument, text, RTF, or CSV — one file at a time.
                        </p>
                        {file && (
                            <div className="program-document-file-name">
                                Selected: <strong>{file.name}</strong>
                            </div>
                        )}
                        {editDocumentId && (
                            <p className="program-document-dropzone-hint" style={{ marginTop: 12 }}>
                                Leave file empty to only update the name.
                            </p>
                        )}
                    </div>

                    {progress != null && (
                        <div className="program-document-progress">
                            <div className="program-document-progress-bar">
                                <div className="program-document-progress-fill" style={{ width: `${progress}%` }} />
                            </div>
                            <div className="program-document-progress-label">{progress}%</div>
                        </div>
                    )}

                    {error && (
                        <div className="program-document-error" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="program-document-actions">
                        <button type="submit" className="program-document-submit" disabled={submitting}>
                            {submitting ? "Working…" : submitLabel}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
